import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, useLoad } from "@/lib/api";
import { useMe } from "@/lib/me";
import { gbp } from "@/lib/format";

function LinkRow({ l, owner, onDone }) {
  const api = useApi();
  const [ref, setRef] = useState(l.suggested || "");
  const [busy, setBusy] = useState("");
  const act = async (what) => {
    setBusy(what);
    try {
      await api("post", `/ref-links/${l.doc_id}/${what}`, what === "approve" ? { ref } : undefined);
      toast.success(what === "approve" ? `Linked to invoice ${ref}` : "Marked as not on this statement");
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 py-3 sm:flex-row sm:items-center" data-testid={`ref-link-${l.doc_id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm"><span className="font-semibold">{l.supplier}</span> · delivery note <span className="font-mono font-semibold">{l.dn_no}</span> · {l.date} · <span className="font-mono">{gbp(l.amount)}</span></p>
        <p className="text-xs text-slate-500">{l.suggested ? <>Suggested invoice <b className="font-mono text-teal-700">{l.suggested}</b>: {l.reason}</> : `No sure match: ${l.reason}. Pick the invoice below.`}</p>
      </div>
      {owner ? (
        <div className="flex flex-wrap items-center gap-2">
          <select value={ref} onChange={(e) => setRef(e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 font-mono text-sm sm:w-64" data-testid={`ref-link-select-${l.doc_id}`}>
            <option value="">Choose invoice…</option>
            {l.options.map((o) => <option key={o.ref} value={o.ref}>{o.ref} · {gbp(o.amount)}{o.ref === l.suggested ? " ★" : ""}</option>)}
          </select>
          <Button size="sm" disabled={!ref || !!busy} onClick={() => act("approve")} data-testid={`ref-link-approve-${l.doc_id}`}>{busy === "approve" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Approve</Button>
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act("reject")} data-testid={`ref-link-reject-${l.doc_id}`}>Not on statement</Button>
        </div>
      ) : <span className="text-xs font-semibold text-amber-700">Waiting for the owner to approve</span>}
    </div>
  );
}

export const RefLinks = ({ refresh, onChanged }) => {
  const me = useMe();
  const list = useLoad("/ref-links");
  const { reload } = list;
  useEffect(() => { if (refresh) reload(); }, [refresh, reload]);
  if (!list.data?.length) return null;
  const done = () => { list.reload(); onChanged?.(); };
  return (
    <div className="mb-6 rounded-3xl border border-sky-200 bg-white p-5 rise" data-testid="ref-links-panel">
      <div className="flex items-center gap-2"><Link2 className="h-5 w-5 text-sky-600" /><h2 className="text-lg font-bold">Delivery notes to link ({list.data.length})</h2></div>
      <p className="mt-1 text-sm text-slate-500">These invoices were saved with only a delivery note number. Confirm which invoice number on the statement each one is. They're left out of queries until you do.</p>
      <div className="mt-2">{list.data.map((l) => <LinkRow key={`${l.doc_id}-${l.suggested}`} l={l} owner={me.role === "owner"} onDone={done} />)}</div>
    </div>
  );
};
